// Runtime asset-URL resolution so a single built artifact is never pinned to one stage's CDN.
//
// The build keeps Vite's `base` at '/'; the CDN host is resolved at request/render time from the live
// env instead:
//   - HTML-embedded URLs (entry <script>, modulepreload, stylesheet <link>) are prefixed by the server
//     entry's `transformAssets` (see src/server.ts).
//   - JS-embedded URLs (dynamic-import chunks, CSS-from-JS like app.css?url) go through Vite's
//     `renderBuiltUrl` runtime form, which emits `globalThis.__assetUrl(...)` (see vite.config.ts).
//   - public/ assets referenced by hand (favicon, font, landing videos/logos, indicator icons, the
//     excelize wasm) go through `assetUrl()` below.
// All three must agree byte-for-byte so SSR and the client's first render match (no hydration churn).
//
// Server reads process.env.URL_CDN; the browser reads window.__CDN_URL, the stash the __root head
// script writes before any chunk loads. Empty (same-origin) in dev (Vite hosts assets) and the e2e
// build (the unified Bun server hosts them — keyed on the canonical E2E_PGLITE runtime flag).
// See: https://vite.dev/guide/build.html#advanced-base-options
//      https://tanstack.com/start/latest/docs/framework/react/guide/cdn-asset-urls

declare global {
  interface Window {
    __CDN_URL?: string
  }
}

/** The CDN origin for this deployment, or '' when assets are same-origin (dev / e2e). No trailing slash. */
export function getRuntimeCdnUrl(): string {
  if (typeof window !== 'undefined') return (window.__CDN_URL ?? '').replace(/\/$/, '')
  // SSR: same-origin in dev (Vite serves assets) and in the e2e build (unified server hosts them).
  if (import.meta.env.DEV) return ''
  if (typeof process !== 'undefined') {
    if (process.env.E2E_PGLITE === 'true') return ''
    return (process.env.URL_CDN ?? '').replace(/\/$/, '')
  }
  return ''
}

/**
 * Resolve a public-dir asset (path relative to the client root, leading slash optional) to its CDN
 * URL in prod or a same-origin '/'-rooted URL otherwise. Mirror of the `globalThis.__assetUrl`
 * function injected in __root for Vite's renderBuiltUrl runtime form — keep the two in lockstep.
 */
export function assetUrl(file: string): string {
  const base = getRuntimeCdnUrl()
  return (base ? `${base}/` : '/') + file.replace(/^\//, '')
}
